import React from 'react';
import { normalizeArray } from './utils/normalize';

const renderBulletList = (items = [], options = {}) => {
  const { listClassName = '', itemClassName = '' } = options;
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <ul className={listClassName}>
      {items.map((item, index) => (
        <li key={index} className={itemClassName}>
          {item}
        </li>
      ))}
    </ul>
  );
};

const ATSResumePreview = ({ componentRef, resumeData = {} }) => {
  const {
    full_name,
    tagline_primary,
    tagline_secondary,
    summary_intro,
    professional_summary,
    looking_to_items,
    expertise_paragraph,
    collaboration_paragraph,
    call_to_action_phone,
    call_to_action_email,
    linkedin,
    whatsapp,
    phone,
    email,
    location,
    key_bullet_sections,
    relevant_experience,
    skills,
    achievements,
    certifications,
    education,
    projects,
    languages,
    interests,
  } = resumeData || {};

  const isTemplateResume = resumeData?.isTemplate !== false;
  const trimText = (value) => (typeof value === 'string' ? value.trim() : '');
  const pickDisplay = (value, fallback = '') => {
    const trimmed = trimText(value);
    if (trimmed) return trimmed;
    return isTemplateResume ? fallback : '';
  };

  // Default template values
  const defaultFullName = 'Harsha Rao';
  const defaultTaglinePrimary = '[ Global AI Corporate Trainer, AI Architect, AI Consultant, AI Researcher, AI Product Dev, MCP, Gen AI Expert, Machine Learning (ML), Deep Learning, Natural Language Processing, GPT, LLM, Copilot, Huggingface, Langchain, Agentic AI, RAG, AI Agents, NVIDIA, Oracle 23AI, Moveworks, Knime, Palantir, N8N, Vibe Code Tools, Cursor, Claude, Anthropic, Gemini, Azure AI, GCP, AWS ]';
  const defaultTaglineSecondary = '[ IT Professional with 20+ years\' experience | AI Product Development Experience of 13+ Years | AI Global Corporate Training Experience of 9+ Years ]';

  // Use pickDisplay for all fields
  const displayFullName = pickDisplay(full_name, defaultFullName);
  const displayTaglinePrimary = pickDisplay(tagline_primary, defaultTaglinePrimary);
  const displayTaglineSecondary = pickDisplay(tagline_secondary, defaultTaglineSecondary);
  const displaySummaryIntro = pickDisplay(summary_intro, '');
  const displayProfessionalSummary = pickDisplay(professional_summary, '');
  const displayExpertiseParagraph = pickDisplay(expertise_paragraph, '');
  const displayCollaborationParagraph = pickDisplay(collaboration_paragraph, '');
  const displayCallToActionPhone = pickDisplay(call_to_action_phone, '');
  const displayCallToActionEmail = pickDisplay(call_to_action_email, '');
  const displayLinkedin = pickDisplay(linkedin, '');
  const displayWhatsapp = pickDisplay(whatsapp, '');
  const displayPhone = pickDisplay(phone, '');
  const displayEmail = pickDisplay(email, '');
  const displayLocation = pickDisplay(location, '');

  // Arrays - only show if not empty or if template
  const displayLookingToItems = Array.isArray(looking_to_items) && looking_to_items.length > 0 
    ? looking_to_items 
    : (isTemplateResume ? [] : []);
  const displayKeyBulletSections = Array.isArray(key_bullet_sections) && key_bullet_sections.length > 0
    ? key_bullet_sections
    : (isTemplateResume ? [] : []);

  const summarySection = (
    <div key="summary" className="ats-section">
      <h2>Profile Summary</h2>
      {displaySummaryIntro && <p className="ats-summary-text">{displaySummaryIntro}</p>}
      {displayProfessionalSummary && (
        <p className="ats-summary-text">{displayProfessionalSummary}</p>
      )}
      {renderBulletList(displayLookingToItems, { itemClassName: 'ats-summary-text' })}
      {displayExpertiseParagraph && <p className="ats-summary-text">{displayExpertiseParagraph}</p>}
      {displayCollaborationParagraph && (
        <p className="ats-summary-text">{displayCollaborationParagraph}</p>
      )}
      {displayCallToActionPhone && (
        <p className="ats-contact-line ats-summary-text">{displayCallToActionPhone}</p>
      )}
      {displayCallToActionEmail && (
        <p className="ats-contact-line ats-summary-text">{displayCallToActionEmail}</p>
      )}
    </div>
  );

  const contactLines = [
    displayPhone ? `Phone: ${displayPhone}` : null,
    displayEmail ? `Email: ${displayEmail}` : null,
    displayLocation ? `Location: ${displayLocation}` : null,
    displayLinkedin ? `LinkedIn: ${displayLinkedin}` : null,
    displayWhatsapp ? `${displayWhatsapp}` : null,
  ].filter(Boolean);

  const contactSection =
    contactLines.length > 0 ? (
      <div key="contact" className="ats-section">
        <h2>Contact Details</h2>
        {renderBulletList(contactLines)}
      </div>
    ) : null;

  const experienceSections = Array.isArray(relevant_experience)
    ? relevant_experience
        .filter(
          (exp) =>
            exp &&
            (exp.position || exp.company || exp.description_points?.length || exp.responsibilities?.length)
        )
        .map((exp, index) => (
          <div key={`experience-${index}`} className="ats-section">
            <h2>Experience – {exp.position || 'Role'}</h2>
            <p>
              {[exp.company, exp.location].filter(Boolean).join(' • ')}
              {exp.start_date || exp.end_date
                ? ` | ${[exp.start_date, exp.end_date || 'Present'].filter(Boolean).join(' - ')}`
                : ''}
            </p>
            {renderBulletList(
              normalizeArray(exp.description_points).length
                ? exp.description_points
                : exp.responsibilities,
              { itemClassName: 'ats-experience-text' }
            )}
          </div>
        ))
    : [];

  const skillsEntries = Object.entries(skills || {}).filter(
    ([, value]) => Array.isArray(value) && value.length > 0
  );

  const skillsSection =
    skillsEntries.length > 0 ? (
      <div key="skills" className="ats-section">
        <h2>Skills Snapshot</h2>
        {skillsEntries.map(([category, values]) => (
          <div key={category}>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>
              {category
                .split('_')
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' ')}
            </p>
            {renderBulletList(values, { itemClassName: 'ats-summary-text' })}
          </div>
        ))}
      </div>
    ) : null;

  const projectsSection =
    Array.isArray(projects) && projects.length > 0 ? (
      <div key="projects" className="ats-section">
        <h2>Projects</h2>
        {projects.map((project, index) => (
          <div key={index} style={{ marginBottom: 12 }}>
            <p style={{ fontWeight: 600, marginBottom: 4 }}>
              {project.title || project.name || `Project ${index + 1}`}
            </p>
            {project.year && <p style={{ marginBottom: 4 }}>Year: {project.year}</p>}
            {renderBulletList(
              Array.isArray(project.description)
                ? project.description
                : normalizeArray(project.description || project.summary)
            , { itemClassName: 'ats-project-text' })}
            {project.link && (
              <p className="ats-contact-line">Link: {project.link}</p>
            )}
          </div>
        ))}
      </div>
    ) : null;

  const achievementsSection =
    Array.isArray(achievements) && achievements.length > 0 ? (
      <div key="achievements" className="ats-section">
        <h2>Achievements</h2>
        {renderBulletList(
          achievements.map((item) =>
            typeof item === 'string' ? item : item?.title || item?.description || ''
          )
        )}
      </div>
    ) : null;

  const certificationsSection =
    Array.isArray(certifications) && certifications.length > 0 ? (
      <div key="certifications" className="ats-section">
        <h2>Certifications</h2>
        {renderBulletList(
          certifications.map((cert) =>
            typeof cert === 'string'
              ? cert
              : [cert?.name, cert?.provider].filter(Boolean).join(' – ')
          )
        )}
      </div>
    ) : null;

  const educationSections = Array.isArray(education)
    ? education
        .filter(
          (edu) => edu && (edu.degree || edu.institution || edu.field_of_study || edu.start_year || edu.end_year)
        )
        .map((edu, index) => (
          <div key={`education-${index}`} className="ats-section">
            <h2>Education – {edu.degree || 'Program'}</h2>
            <p>
              {[edu.institution || edu.institute, edu.location].filter(Boolean).join(' • ')}
              {edu.start_year || edu.end_year
                ? ` | ${[edu.start_year, edu.end_year || 'Present'].filter(Boolean).join(' - ')}`
                : ''}
            </p>
            {edu.field_of_study && <p>Field of Study: {edu.field_of_study}</p>}
            {edu.cgpa && <p>CGPA: {edu.cgpa}</p>}
            {renderBulletList(edu.coursework, { itemClassName: 'ats-summary-text' })}
          </div>
        ))
    : [];

  const languagesSection =
    Array.isArray(languages) && languages.length > 0 ? (
      <div key="languages" className="ats-section">
        <h2>Languages</h2>
        {renderBulletList(languages, { itemClassName: 'ats-summary-text' })}
      </div>
    ) : null;

  const interestsSection =
    Array.isArray(interests) && interests.length > 0 ? (
      <div key="interests" className="ats-section">
        <h2>Interests</h2>
        {renderBulletList(interests, { itemClassName: 'ats-summary-text' })}
      </div>
    ) : null;

  const highlightSections = displayKeyBulletSections
    ?.filter((section) => section && (section.title || (section.items && section.items.length)))
    .map((section, index) => (
      <div key={`highlight-${index}`} className="ats-section">
        {section.title && <div className="ats-highlight-title">{section.title}</div>}
        {renderBulletList(section.items, { itemClassName: 'ats-project-text' })}
      </div>
    )) || [];

  const orderedSections = [
    summarySection,
    contactSection,
    ...experienceSections,
    skillsSection,
    projectsSection,
    achievementsSection,
    certificationsSection,
    ...educationSections,
    languagesSection,
    interestsSection,
    ...highlightSections,
  ].filter(Boolean);

  return (
    <div id="section-to-print" className="ats-resume-preview" ref={componentRef}>
      <style>{`
        .ats-resume-preview {
          width: 100%;
          padding: 20px;
          box-sizing: border-box;
          display: flex;
          justify-content: center;
          background: transparent;
        }
        .ats-page {
          width: 210mm;
          max-width: 210mm;
          min-height: 297mm;
          padding: 25mm 25mm 30mm;
          box-sizing: border-box;
          background: #ffffff;
          font-family: 'Arial', 'Helvetica Neue', Helvetica, sans-serif;
          color: #1f2937;
          line-height: 1.55;
          font-size: 11pt;
          position: relative;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          margin: 0 auto;
          overflow: visible;
        }
        .ats-header {
          text-align: center;
          margin-bottom: 24px;
        }
        .ats-header h1 {
          font-size: 30pt;
          letter-spacing: 0.5px;
          margin: 0 0 12px;
          color: #1f2937;
        }
        .ats-tagline {
          font-size: 11pt;
          color: #1f3d7a;
          margin: 6px 0;
          white-space: pre-line;
        }
        .ats-divider {
          border: none;
          border-top: 2px solid #111827;
          margin: 24px 0;
        }
        .ats-section {
          margin-bottom: 22px;
          page-break-inside: avoid;
        }
        .ats-section h2 {
          font-size: 13pt;
          margin: 0 0 8px;
          color: #111827;
          text-transform: none;
        }
        .ats-section p {
          margin: 0 0 10px;
          white-space: pre-line;
        }
        .ats-section ul {
          margin: 8px 0 12px 22px;
          padding: 0;
        }
        .ats-section ul li {
          margin-bottom: 6px;
        }
        .ats-summary-text {
          color: #3b82f6;
        }
        .ats-experience-text {
          color: #16a34a;
        }
        .ats-project-text {
          color: #2563eb;
        }
        .ats-highlight-title {
          font-size: 12pt;
          font-weight: 600;
          margin: 18px 0 6px;
          color: #0f172a;
        }
        .ats-contact-line {
          font-weight: 600;
          color: #0b3b74;
          margin: 4px 0;
        }
        .ats-emphasis {
          color: #b91c1c;
          font-weight: 600;
        }
        @media print {
          @page {
            size: A4;
            margin: 0;
            background: white;
          }
          
          html, body {
            background: white !important;
            margin: 0;
            padding: 0;
          }
          
          #section-to-print {
            background: white !important;
          }
          
          .ats-resume-preview {
            padding: 0;
            background: white !important;
          }
          
          .ats-page {
            width: 210mm !important;
            max-width: 210mm !important;
            min-height: 297mm !important;
            padding: 20mm !important;
            box-shadow: none !important;
            margin: 0 auto !important;
            overflow: visible !important;
            page-break-after: always !important;
            background: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          
          .ats-page:last-child {
            page-break-after: auto !important;
            background: white !important;
          }
        }
        
        /* Ensure A4 size in preview - scale down if needed for smaller screens */
        @media screen and (max-width: 900px) {
          .ats-page {
            width: 100%;
            max-width: 210mm;
            transform: scale(0.8);
            transform-origin: top center;
          }
        }
      `}</style>

      <div className="ats-page">
        <div className="ats-header">
          <h1>{displayFullName}</h1>
          {displayTaglinePrimary && (
            <div className="ats-tagline">{displayTaglinePrimary}</div>
          )}
          {displayTaglineSecondary && (
            <div className="ats-tagline">{displayTaglineSecondary}</div>
          )}
        </div>
        <hr className="ats-divider" />
        {orderedSections}
      </div>
    </div>
  );
};

export default ATSResumePreview;
